import { colorPrimary } from '@atlas-frontend/design-tokens';
import React, { FC, memo, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { TextEllipsis } from '../../common/components';
import { TextBadge } from '../../common/components/TextBadge';
import {
  AlternativeAlgorithmDataType,
  DataObjectValidFormDataType,
} from '../schema';
import { AlternativeButton } from './AlternativeButton';
import { ViewableGroupOrAlgorithm } from './types';

export interface AlternativesFormCellProps {
  rowIndex: number;
  onSelectGroupOrAlgorithm: (viewable: ViewableGroupOrAlgorithm) => void;
}

export const AlternativesFormCell: FC<AlternativesFormCellProps> = memo(
  (props) => {
    const { rowIndex, ...rest } = props;
    const { getValues } = useFormContext<DataObjectValidFormDataType>();
    const alternatives = getValues(
      `alternativeAlgorithmSets.${rowIndex}.alternativeAlgorithms`,
    );
    const alternativeCode = getValues(
      `alternativeAlgorithmSets.${rowIndex}.code`,
    );
    return (
      <AlternativesCell
        alternatives={alternatives}
        alternativeCode={alternativeCode}
        {...rest}
      />
    );
  },
);

export interface AlternativesCellProps {
  alternatives: AlternativeAlgorithmDataType[];
  alternativeCode: number;
  onSelectGroupOrAlgorithm: (viewable: ViewableGroupOrAlgorithm) => void;
}

export const AlternativesCell: FC<AlternativesCellProps> = memo((props) => {
  const { alternatives, alternativeCode, onSelectGroupOrAlgorithm } = props;

  const alternativeLabels = useMemo(
    () =>
      alternatives.map((alternative, index, { length }) => {
        const { groupCode, code, defaultAlgorithm } = alternative;

        let key: string;
        let name: React.ReactNode;
        let onClick: (() => void) | undefined;

        if (groupCode) {
          key = `alt-${groupCode}`;
          name = groupCode;
          onClick = () =>
            onSelectGroupOrAlgorithm({ groupCode, alternativeCode });
        } else if (code) {
          key =
            (alternative?.id ?? 0) > 0
              ? `alt-id-${alternative.id}`
              : `alt-index-${index}`;
          name = code;
          onClick = () => onSelectGroupOrAlgorithm({ code, alternativeCode });
        } else {
          key =
            (alternative?.id ?? 0) > 0
              ? `alt-id-${alternative.id}`
              : `alt-index-${index}`;
          name = '';
        }

        if (onClick) {
          name = (
            <AlternativeButton id={key} type="link" onClick={onClick}>
              {name}
            </AlternativeButton>
          );
        }

        return (
          <span key={key}>
            {defaultAlgorithm ? (
              <TextBadge color="inherit">{name}</TextBadge>
            ) : (
              name
            )}
            {index < length - 1 && <Comma>,</Comma>}
          </span>
        );
      }),
    [alternatives, alternativeCode, onSelectGroupOrAlgorithm],
  );

  return (
    <AlternativesContainer rows={2} tooltip={alternativeLabels}>
      {alternativeLabels}
    </AlternativesContainer>
  );
});

const AlternativesContainer = styled(TextEllipsis)`
  margin-bottom: 0 !important;
  -webkit-line-clamp: 1 !important;
  color: ${colorPrimary};
`;

const Comma = styled.span`
  margin-right: 4px;
`;
